/// <reference types="@better-auth/sso" />
import { TaskAssignee } from "@repo/api/client";
import { getSharedConfig, TENANT_SLUG_HEADER } from "@repo/config/sharedConfig";
import {
	createNamedContext,
	useRequiredContext,
} from "@repo/utils/hooks/useRequiredContext";
import { tag } from "@repo/utils/typeUtils";
import { inferAdditionalFields } from "better-auth/client/plugins";
import { createAuthClient } from "better-auth/react";
import { ssoClient } from "@better-auth/sso/client";
import { useMemo, type ReactNode } from "react";
import { User as DatabaseUser, DepartmentId, UserId } from "../db/schema";
import { Flex, Text } from "@repo/ui";
import { absoluteRoute, Routes } from "../routing";
import { parseTaskListFilter } from "../taskListFilter";
import { getTenantSlug } from "../tenant";

const sharedConfig = getSharedConfig(import.meta.env.MODE);

/**
 * Exported so non-React code can sign out — ApiContext's fetch wrapper does this
 * when the session cookie for this host belongs to another tenant.
 */
export const authClient = createAuthClient({
	baseURL: sharedConfig.serverUrl,
	fetchOptions: {
		onRequest(context) {
			const tenantSlug = getTenantSlug();
			if (tenantSlug) {
				const headers = new Headers(context.headers);
				headers.set(TENANT_SLUG_HEADER, tenantSlug);
				return { ...context, headers };
			}
			return context;
		},
	},
	plugins: [
		inferAdditionalFields({
			user: {
				departmentId: {
					type: "string",
					required: false,
					input: false,
				},
				taskAssigneeFilter: {
					type: "string",
					required: false,
					input: false,
				},
				statusFilter: {
					type: "string",
					required: false,
					input: false,
				},
			},
		}),
		ssoClient(),
	],
});

/**
 * Drop the Tandem client and in-memory session by leaving the page. A SPA
 * sign-out leaves sync pulling with a stale clientId, which 500s as
 * RECONNECT_ERROR after the next login.
 */
export async function signOutAndReload(): Promise<void> {
	try {
		await authClient.signOut();
	} catch (error) {
		console.error("Failed to sign out", {
			error: error instanceof Error ? error.message : String(error),
		});
	}
	window.location.assign(absoluteRoute(Routes.login));
}

type AuthClient = typeof authClient;

export type User = AuthClient["$Infer"]["Session"]["user"];
export type Session = AuthClient["$Infer"]["Session"];

type AuthContextValue = AuthClient;

const AuthContext = createNamedContext<AuthContextValue | undefined>(
	"AuthContext",
	undefined,
);

type AuthProviderProps = {
	children: ReactNode;
};

export function AuthProvider({ children }: AuthProviderProps) {
	const { isPending } = authClient.useSession();

	if (isPending) {
		return (
			<Flex
				flex
				align="center"
				justify="center"
				style={{ height: "100vh", width: "100vw" }}
			>
				<Text weight="medium" shimmer>
					Loading
				</Text>
			</Flex>
		);
	}

	return (
		<AuthContext.Provider value={authClient}>{children}</AuthContext.Provider>
	);
}

export function useAuthClient(): AuthClient {
	const context = useRequiredContext(AuthContext);
	return context;
}

export function useLoggedInUser(): DatabaseUser | undefined {
	const authClient = useAuthClient();
	const { data } = authClient.useSession();

	// Case better-auth user to local database schema user
	const user = useMemo(() => {
		if (!data?.user) return undefined;
		return {
			id: tag<UserId>(data.user.id),
			name: data.user.name,
			email: data.user.email,
			imageUrl: data.user.image || undefined,
			departmentId: data.user.departmentId
				? tag<DepartmentId>(data.user.departmentId)
				: undefined,
			taskAssigneeFilter:
				(data.user.taskAssigneeFilter as TaskAssignee) || TaskAssignee.MyTasks,
			// LeftPane reads the filter from here, not the synced store, so this is
			// the value that has to be safe to index the filter config with.
			statusFilter: parseTaskListFilter(data.user.statusFilter),
		};
	}, [data]);

	return user;
}
